import { z } from 'zod';

import {
  IdentifierInputSchema,
  PurchaseCashSessionResolutionSchema,
  PurchasePrintAttemptOutcomeSchema,
  PrintStatusSchema,
  PurchaseCompletionPrintActionSchema,
  PurchaseCommittedSnapshotSchema,
  PurchaseIdentifierTypeSchema,
  PurchaseModeSummarySchema,
} from './purchases.schema.js';
import {
  PurchaseSettlementAvailabilitySchema,
  PurchaseSettlementMethodSchema,
  PurchaseSettlementSelectionInputSchema,
} from './purchases-settlement.schema.js';
import {
  CustomerIdentifierSnapshotSchema,
  CustomerSnapshotSchema,
} from '../customers/customers.schema.js';
import type {
  CommitPurchaseRequest,
  CommitPurchaseResponse,
  RecordTrainingPurchaseRequest,
  RecordTrainingPurchaseResponse,
  PreflightPayRequest,
  PreflightPayResponse,
  PurchaseLimitSummary,
} from './purchases-commit.dto.js';

const ApiErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.unknown().optional(),
  traceId: z.string(),
});

function validateSettlementMethodAlias(
  value: {
    settlement?: { method: unknown } | null;
    settlementMethod?: unknown;
  },
  ctx: z.RefinementCtx,
): void {
  const structuredMethod = value.settlement?.method ?? null;
  const legacyMethod = value.settlementMethod ?? null;
  if (structuredMethod && legacyMethod && structuredMethod !== legacyMethod) {
    ctx.addIssue({
      code: 'custom',
      message: 'settlementMethod must match settlement.method when both are provided.',
      path: ['settlementMethod'],
    });
  }
}

export const PurchaseLimitSummarySchema: z.ZodType<PurchaseLimitSummary> = z.object({
  materialId: z.uuid(),
  limitLbs: z.number().nullable(),
  usedEquivalentLbs: z.number().nullable(),
  requestedEquivalentLbs: z.number().nullable(),
  remainingEquivalentLbs: z.number().nullable(),
  isAllowed: z.boolean(),
});

export const PreflightPayRequestSchema: z.ZodType<PreflightPayRequest> = z
  .object({
    companyId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    laneId: z.string().trim().min(1).max(64).nullable().optional(),
    cashSessionId: z.uuid().nullable().optional(),
    settlement: PurchaseSettlementSelectionInputSchema.nullable().optional(),
    settlementMethod: PurchaseSettlementMethodSchema.nullable().optional(),
  })
  .superRefine(validateSettlementMethodAlias);

export const PreflightPayResponseSchema: z.ZodType<PreflightPayResponse> = z.object({
  requiresIdentifier: z.boolean(),
  allowedIdentifierTypes: z.array(PurchaseIdentifierTypeSchema),
  outOfStateCapApplies: z.boolean(),
  remainingLimits: z.array(PurchaseLimitSummarySchema),
  errors: z.array(ApiErrorSchema),
  warnings: z.array(ApiErrorSchema).optional(),
  cashSessionResolution: PurchaseCashSessionResolutionSchema.optional(),
  settlementAvailability: PurchaseSettlementAvailabilitySchema,
});

export const CommitPurchaseRequestSchema: z.ZodType<CommitPurchaseRequest> = z
  .object({
    companyId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    laneId: z.string().trim().min(1).max(64).nullable().optional(),
    commitRequestId: z.uuid().nullable().optional(),
    completionPrintAction: PurchaseCompletionPrintActionSchema,
    occurredAt: z.iso.datetime().optional(),
    identifier: IdentifierInputSchema.nullable().optional(),
    customerId: z.uuid().nullable().optional(),
    customerSnapshot: CustomerSnapshotSchema.nullable().optional(),
    identifierSnapshot: CustomerIdentifierSnapshotSchema.nullable().optional(),
    cashSessionId: z.uuid().nullable().optional(),
    settlement: PurchaseSettlementSelectionInputSchema.nullable().optional(),
    settlementMethod: PurchaseSettlementMethodSchema.nullable().optional(),
  })
  .superRefine(validateSettlementMethodAlias);

export const CommitPurchaseResponseSchema: z.ZodType<CommitPurchaseResponse> = z.object({
  purchase: PurchaseCommittedSnapshotSchema,
  printStatus: PrintStatusSchema,
  warnings: z.array(ApiErrorSchema).optional(),
  printAttemptOutcome: PurchasePrintAttemptOutcomeSchema.optional(),
  printAttemptMessage: z.string().trim().min(1).nullable().optional(),
});

export const RecordTrainingPurchaseRequestSchema: z.ZodType<RecordTrainingPurchaseRequest> =
  z.object({
    companyId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    laneId: z.string().trim().min(1).max(64).nullable().optional(),
    occurredAt: z.iso.datetime().optional(),
    identifier: IdentifierInputSchema.nullable().optional(),
  });

export const RecordTrainingPurchaseResponseSchema: z.ZodType<RecordTrainingPurchaseResponse> =
  z.object({
    summary: PurchaseModeSummarySchema,
  });
